/**
 * TracesPage - Opik Trace Viewer
 * Main page with trace list on the left, detail panel on the right
 */

import { useState } from 'react';
import { AlertCircle, ExternalLink } from 'lucide-react';
import { TraceList, TraceDetail, TraceMetrics, useTracesStatus } from '@/features/traces';
import { useI18n } from '@/i18n';

type TabType = 'list' | 'metrics';

export function TracesPage() {
  const [activeTab, setActiveTab] = useState<TabType>('list');
  const [selectedTraceId, setSelectedTraceId] = useState<string | null>(null);
  const { data: status, isLoading: statusLoading } = useTracesStatus();
  const { messages } = useI18n();
  const t = messages.tracesPage;

  // Loading state — prevent flash of content before status resolves
  if (statusLoading) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{t.pageTitle}</h1>
            <div className="h-4 w-40 bg-gray-100 rounded mt-1 animate-pulse" />
          </div>
          <div className="h-8 w-48 bg-gray-100 rounded-lg animate-pulse" />
        </div>
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
          {t.loading}
        </div>
      </div>
    );
  }

  // Not configured state
  if (status && !status.configured) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-md p-8">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">{t.notConfigured}</h2>
          <p className="text-sm text-gray-600">
            {t.notConfiguredDesc}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Page header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{t.pageTitle}</h1>
          {status && (
            <p className="text-xs text-gray-500 mt-0.5">
              {status.workspace} / {status.project_name}
            </p>
          )}
        </div>
        <div className="flex items-center gap-4">
          {/* Tabs */}
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setActiveTab('list')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                activeTab === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t.tabList}
            </button>
            <button
              onClick={() => setActiveTab('metrics')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                activeTab === 'metrics' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t.tabMetrics}
            </button>
          </div>
          {/* External link to Opik console */}
          <a
            href="https://www.comet.com/opik"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600"
          >
            {t.opikConsole}
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'metrics' ? (
        <TraceMetrics />
      ) : (
        <div className="flex-1 flex overflow-hidden">
          {/* Left: Trace list */}
          <div className={`border-r border-gray-200 bg-white overflow-hidden transition-all ${
            selectedTraceId ? 'w-1/2 lg:w-2/5' : 'w-full'
          }`}>
            <TraceList
              onSelectTrace={setSelectedTraceId}
              selectedTraceId={selectedTraceId}
            />
          </div>

          {/* Right: Detail panel */}
          {selectedTraceId && (
            <div className="flex-1 bg-white overflow-hidden">
              <TraceDetail
                traceId={selectedTraceId}
                onClose={() => setSelectedTraceId(null)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
